import { useEffect, useState } from 'preact/hooks';
import type { EnvelopeV1 } from '../../shared/types';
import { ApiError, apiDelete, apiGet, apiPut } from './api';
import { authenticateWithPrf } from './webauthn-client';
import {
  generateDek, makePassphraseWrapper, makePrfWrapper, openEnvelope, sealEnvelope,
  unlockWithPassphrase, unlockWithPrf, withWrapper, type WalletEntry,
} from './vault';

/** In-memory only. A reload locks the vault again; nothing decrypted is ever persisted. */
export type VaultStatus = 'loading' | 'none' | 'locked' | 'unlocked';
export type VaultState = { status: VaultStatus; envelope: EnvelopeV1 | null; dek: CryptoKey | null; addresses: WalletEntry[] };

let state: VaultState = { status: 'loading', envelope: null, dek: null, addresses: [] };
const listeners = new Set<() => void>();
function set(patch: Partial<VaultState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

export function useVault(): VaultState {
  const [s, setS] = useState(state);
  useEffect(() => {
    const l = () => setS(state);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);
  return s;
}

let loaded = false;
export async function loadEnvelope(force = false): Promise<void> {
  if (loaded && !force) return;
  loaded = true;
  try {
    const env = await apiGet<EnvelopeV1>('/api/wallet/envelope');
    set({ envelope: env, status: state.dek && state.envelope?.updatedAt === env.updatedAt ? 'unlocked' : 'locked' });
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) set({ status: 'none', envelope: null, dek: null, addresses: [] });
    else throw e;
  }
}

async function put(env: EnvelopeV1, ifMatch: string): Promise<EnvelopeV1> {
  try {
    const { updatedAt } = await apiPut<{ updatedAt: number }>('/api/wallet/envelope', env, { 'If-Match': ifMatch });
    return { ...env, updatedAt };
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) {
      await loadEnvelope(true);
      throw new Error('The vault changed on another device. Reloaded; unlock again and retry.');
    }
    throw e;
  }
}

async function unlockedWith(dek: CryptoKey) {
  const env = state.envelope!;
  const payload = await openEnvelope(env, dek);
  set({ status: 'unlocked', dek, addresses: payload.addresses });
}

export async function unlockWithPasskey(): Promise<void> {
  if (!state.envelope) throw new Error('No vault yet.');
  const { credId, prf } = await authenticateWithPrf();
  await unlockedWith(await unlockWithPrf(state.envelope, credId, prf));
}

export async function unlockWithPass(pass: string): Promise<void> {
  if (!state.envelope) throw new Error('No vault yet.');
  await unlockedWith(await unlockWithPassphrase(state.envelope, pass));
}

export async function createVault(addresses: WalletEntry[], opts: { passkey: boolean; passphrase?: string }): Promise<void> {
  if (!opts.passkey && !opts.passphrase) throw new Error('Choose at least one way to unlock.');
  const dek = await generateDek();
  const wrapped = [];
  if (opts.passkey) {
    const { credId, prf } = await authenticateWithPrf();
    wrapped.push(await makePrfWrapper(dek, credId, prf));
  }
  if (opts.passphrase) wrapped.push(await makePassphraseWrapper(dek, opts.passphrase));
  const env = await put(await sealEnvelope({ addresses }, dek, wrapped), '*');
  set({ status: 'unlocked', envelope: env, dek, addresses });
}

export async function saveAddresses(addresses: WalletEntry[]): Promise<void> {
  const { envelope, dek } = state;
  if (!envelope || !dek) throw new Error('Vault is locked.');
  const sealed = await sealEnvelope({ addresses }, dek, envelope.wrapped);
  const env = await put(sealed, String(envelope.updatedAt));
  set({ envelope: env, addresses });
}

export async function addPassphrase(pass: string): Promise<void> {
  const { envelope, dek } = state;
  if (!envelope || !dek) throw new Error('Vault is locked.');
  const env = await put(withWrapper(envelope, await makePassphraseWrapper(dek, pass)), String(envelope.updatedAt));
  set({ envelope: env });
}

/** Wrap the DEK for the passkey the user authenticates with now (e.g. a second device). */
export async function addPasskeyWrapper(): Promise<void> {
  const { envelope, dek } = state;
  if (!envelope || !dek) throw new Error('Vault is locked.');
  const { credId, prf } = await authenticateWithPrf();
  const env = await put(withWrapper(envelope, await makePrfWrapper(dek, credId, prf)), String(envelope.updatedAt));
  set({ envelope: env });
}

export function lockVault(): void {
  set({ status: state.envelope ? 'locked' : 'none', dek: null, addresses: [] });
}

export async function forgetVault(): Promise<void> {
  if (state.envelope) await apiDelete('/api/wallet/envelope', { 'If-Match': String(state.envelope.updatedAt) });
  set({ status: 'none', envelope: null, dek: null, addresses: [] });
}
